import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {isAnalysisTemplatesEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import RunAnalysisTemplateModal from 'analysis-tool/create-analysis-modal/RunAnalysisTemplateModal';
import BenchlingConfig from 'BenchlingConfig';
import DataCatalogItemDetails, {DataCatalogItemActionIcon} from 'data-catalog/DataCatalogItemDetails';
import DataCatalogOrigin from 'data-catalog/DataCatalogOrigin';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import {updateFile} from 'files/network/FileDAO';
import {isSupportedMimeType} from 'files/preview/FilePreviewConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {fileStatusDisplay} from 'instruments/connection-files/ConnectionFilesStatus';
import {renderFilePreview} from 'pipelines/files/ScratchFilePaneContent';
import legacyRunModal from 'ui-library/legacyRunModal';
import {formatBytes} from 'util/NameUtil';
import removeNulls from 'util/removeNulls';

import {FileViewQuery} from './__generated/FileViewQuery';

type File = ExtractByTypename<FileViewQuery['file'], 'FileNode'>;

interface FileDetailsProps {
  file: File;
  reload: () => void;
}

export default function FileDetails({file, reload}: FileDetailsProps): JSX.Element {
  const filePreview = renderFilePreview(file.scratchFile);
  const hasPreviewableContent = isSupportedMimeType(file.scratchFile.mimeType, file.scratchFile.filename);

  const extraColumns = [
    {label: 'Status', value: fileStatusDisplay(file, false)},
    {label: 'Benchling File ID', value: file.displayId},
    {label: 'Size', value: file.scratchFile.size ? formatBytes(file.scratchFile.size) : ''},
    {
      label: 'Origin',
      value: <DataCatalogOrigin originType={file.originType} lineageParent={file.lineageParent} />,
    },
    {label: 'Original File Name', value: file.scratchFile.filename},
  ];

  const actionIcons: Array<DataCatalogItemActionIcon | null> = [
    isAnalysisTemplatesEnabled() && BenchlingConfig.INSTRUMENTS_ENABLE
      ? {
          title: 'Run analysis template',
          icon: 'analytics_analysis',
          onClick: () =>
            legacyRunModal(RunAnalysisTemplateModal, {
              variableDefaults: {FILE: file.id},
              initialAnalysisName: `Analyze ${file.name}`,
            }),
        }
      : null,
    {
      title: 'Download original file',
      icon: 'download',
      disabled: file.scratchFile.externalFileId == null || file.scratchFile.downloadUrl == null,
      onClick: async (): Promise<void> => {
        const signedURL = await performSignedFileQuery(
          file.scratchFile.externalFileId!,
          file.scratchFile.downloadUrl!,
          file.id
        );
        if (signedURL) {
          window.open(signedURL, '_blank', 'noopener noreferrer');
        }
      },
    },
  ];

  return (
    <DataCatalogItemDetails
      dataCatalogItem={file}
      extraColumns={extraColumns}
      metadataTitle="File Metadata"
      tagsEmptyMessage="This file has no custom fields."
      onChange={async (updateParams: {
        id: string;
        name?: string;
        folderId?: string;
        customFields?: {[name: string]: string};
      }): Promise<void> => {
        await updateFile(updateParams);
        reload();
      }}
      actionIcons={removeNulls(actionIcons)}
      hasContent={hasPreviewableContent}
      preferenceKey={`file:${file.scratchFile.mimeType ?? 'unknown'}`}
    >
      <div className={css(styles.filePreview)}>{filePreview}</div>
    </DataCatalogItemDetails>
  );
}

const styles = StyleSheet.create({
  filePreview: {
    paddingTop: 12,
    height: '80vh',
    minHeight: 400,
  },
});
